import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import { MessageSquare, Search, ChevronDown, Mail, Phone } from 'lucide-react';

function formatDate(ts) {
  if (!ts || typeof ts !== 'string' || !ts.includes('T')) return ts || '—';
  const [d, t] = ts.split('T');
  return `${d} · ${t.slice(0, 5)}`;
}

function ContactRow({ msg, isOpen, onToggle }) {
  return (
    <div className="card-moj overflow-hidden" data-testid={`msg-row-${msg.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-4 hover:bg-moj-cream-soft transition flex items-center gap-4"
        aria-expanded={isOpen}
      >
        <div className="w-10 h-10 rounded-full gradient-purple flex items-center justify-center flex-shrink-0">
          <span className="font-serif text-moj-gold text-sm">
            {(msg.full_name || '?').charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-moj-purple-deep flex flex-wrap items-center gap-2">
            {msg.full_name}
            <span className="text-[10px] uppercase tracking-wider font-bold bg-moj-gold/20 text-moj-gold-deep px-1.5 py-0.5 rounded">
              {msg.message_type}
            </span>
          </div>
          <div className="text-xs text-moj-charcoal/60 flex flex-wrap gap-x-3 mt-0.5">
            {msg.email && (
              <span className="inline-flex items-center gap-1">
                <Mail size={12} /> {msg.email}
              </span>
            )}
            {msg.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone size={12} /> {msg.phone}
              </span>
            )}
          </div>
        </div>
        <div className="hidden md:block text-xs text-moj-charcoal/60 text-right">
          {formatDate(msg.created_at)}
        </div>
        <ChevronDown size={18} className={`text-moj-gold-deep transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="px-5 pb-5 pt-3 bg-moj-cream-soft border-t border-moj-gold/20 text-sm">
          <div
            className="rounded-lg bg-white border border-moj-gold/30 p-4 text-moj-charcoal whitespace-pre-wrap leading-relaxed"
            data-testid={`msg-body-${msg.id}`}
          >
            {msg.message}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MessagesTab() {
  const { data: msgs = [], isLoading } = useQuery({
    queryKey: ['adm-msgs'],
    queryFn: async () => (await api.get('/admin/contact-messages')).data,
  });
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return msgs;
    const q = search.toLowerCase();
    return msgs.filter((m) =>
      (m.full_name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.phone || '').toLowerCase().includes(q) ||
      (m.message_type || '').toLowerCase().includes(q) ||
      (m.message || '').toLowerCase().includes(q)
    );
  }, [msgs, search]);

  return (
    <div className="space-y-6">
      <div className="card-moj p-5">
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare size={22} className="text-moj-gold-deep" />
          <h2 className="font-serif text-xl text-moj-purple-deep">Contact Messages</h2>
        </div>
        <p className="text-sm text-moj-charcoal/70">
          Enquiries submitted via the public Contact page. Prayer requests now live in a
          dedicated tab.
        </p>
      </div>

      <div className="card-moj p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-moj-gold-deep" />
          <input
            className="input-moj pl-9"
            placeholder="Search by name, email, phone, type, or content…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="msg-search"
          />
        </div>
        <div className="text-sm text-moj-charcoal/70">
          {filtered.length} of {msgs.length}{' '}
          {msgs.length === 1 ? 'message' : 'messages'}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-moj-charcoal/60">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-moj p-10 text-center text-moj-charcoal/60">
          {msgs.length === 0
            ? 'No contact messages received yet.'
            : 'No messages match your search.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <ContactRow
              key={m.id}
              msg={m}
              isOpen={openId === m.id}
              onToggle={() => setOpenId((cur) => (cur === m.id ? null : m.id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
